import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, Lock } from 'lucide-react';
import { BOOKING_POLICY, quote, validateBooking } from 'types';
import CostSummary from '../components/CostSummary';
import { confirmFree, createInvoice } from '../lib/api';
import { donationOf, toRequest, useBooking } from '../state/BookingContext';

/** Shows the booking summary and creates the (unpaid) Stripe invoice for it. */
export default function ReviewPage() {
  const navigate = useNavigate();
  const { draft, update } = useBooking();
  const request = toRequest(draft);
  const fingerprint = JSON.stringify(request);
  const invalid = validateBooking(request) || (draft.donate && !(donationOf(draft) >= 1));
  const invoice = draft.invoice?.fingerprint === fingerprint ? draft.invoice : undefined;
  const requested = useRef('');
  const [attempt, setAttempt] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (invalid) return navigate('/', { replace: true });
    if (invoice || requested.current === fingerprint + attempt) return;
    requested.current = fingerprint + attempt;
    setError(null);
    // Editing the booking after an invoice exists voids the old one and creates a new one.
    createInvoice({ ...request, replaceInvoiceId: draft.invoice?.invoiceId })
      .then((inv) => update({ invoice: { ...inv, fingerprint } }))
      .catch((e) => setError(e.message));
  }, [fingerprint, attempt]);

  const confirmWithoutPayment = async () => {
    setBusy(true);
    try {
      const { reservationId } = await confirmFree(request);
      navigate(`/result?free=${reservationId}`);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  if (invalid) return null;
  const q = invoice?.quote ?? quote(request);

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <h2 className="text-xl font-semibold text-gray-800">Review your booking</h2>
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 text-sm text-gray-700">
        <p><span className="font-semibold">Name:</span> {request.customer.name}</p>
        <p><span className="font-semibold">Email:</span> {request.customer.email}</p>
      </div>

      <CostSummary quote={q} showFee>
        {q.totalCents > 0 && (
          <p className="mt-4 text-sm text-gray-700 bg-gray-50 rounded-lg p-3 flex gap-2">
            <Lock className="w-4 h-4 mt-0.5 shrink-0" />
            <span>Your payment is processed by Stripe, which ensures a seamless and secure checkout. We never see or store your card details.</span>
          </p>
        )}
        <div className="mt-4 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="font-semibold flex items-center gap-1.5 mb-1.5">
            <AlertTriangle className="w-4 h-4 shrink-0" aria-hidden="true" /> Booking policy - please read before continuing
          </p>
          <ul className="list-disc list-inside space-y-1">
            {BOOKING_POLICY.map((fact) => <li key={fact}>{fact}</li>)}
            {q.totalCents > 0 && <li>You will receive an itemised invoice once payment is complete - please keep a copy, printed or digital, with you at the event.</li>}
          </ul>
        </div>
        {error && <p role="alert" className="mt-4 text-sm text-red-600">{error}</p>}

        {error && !invoice ? (
          <button onClick={() => setAttempt(attempt + 1)} className="mt-4 w-full bg-orange-600 text-white py-3 rounded-lg font-semibold">Try again</button>
        ) : !invoice ? (
          <p className="mt-4 text-sm text-gray-600 text-center">Preparing your invoice…</p>
        ) : invoice.free ? (
          <button onClick={confirmWithoutPayment} disabled={busy} className="mt-4 w-full bg-green-600 text-white py-3 rounded-lg font-semibold disabled:opacity-50">
            {busy ? 'Confirming…' : 'Confirm reservation (nothing to pay)'}
          </button>
        ) : (
          <button onClick={() => navigate('/checkout')} className="mt-4 w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700">Proceed to payment</button>
        )}
      </CostSummary>
      <Link to="/" className="block text-center text-sm text-orange-700 underline">← Change my booking</Link>
    </div>
  );
}
